import { Checkbox, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useRef, useState } from "react";

interface ReservationRequest {
  Name?: string;
  Position?: string;
  Class?: string;
  TickNumber?: string;
  TripBusiness?: string | any[];
  key?: string;
}

interface ExpandedRow extends ReservationRequest {
  tripIndex: number;
  totalTrips: number;
  tripBusinessItem: any;
  rowSpan?: number;
  originalIndex?: number;
}

interface ReservationRequestTableProps {
  reservationRequests: any[];
  setProcessedData: (value: any) => void;
  processedData: any;
}

const ReservationRequestTable = ({
  reservationRequests = [],
  setProcessedData,
  processedData,
}: ReservationRequestTableProps) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // Track if we've already initialized selectedRowKeys from processedData (for edit mode)
  const hasInitializedFromProcessedData = useRef(false);

  const formatTripBusiness = (trip: any): string => {
    if (!trip) return "-";

    if (typeof trip === "string") {
      return trip;
    }

    const from = trip?.From || trip?.from || "";
    const to = trip?.To || trip?.to || "";
    const date = trip?.Date || trip?.date || "";
    const time = trip?.Time || trip?.time || "";

    if (from && to && date) {
      // return `Nơi đi: ${from}, Nơi đến: ${to}, Ngày: ${date}${time ? ` ${time}` : ""}`;
      return `${from} - ${to} (${date} ${time})`;
    }

    return JSON.stringify(trip);
  };

  // Transform data: mỗi hành trình = 1 row
  const expandedDataSource = useMemo(() => {
    const result: ExpandedRow[] = [];

    (reservationRequests || []).forEach((item, itemIndex) => {
      const trips = Array.isArray(item.TripBusiness)
        ? item.TripBusiness
        : item.TripBusiness
        ? [item.TripBusiness]
        : [{ From: "", To: "", Date: "" }];

      const parentKey = `${item.Name}-${item.Position}-${item.Department}`;

      trips.forEach((trip: any, tripIndex: number) => {
        const childKey = `${trip.from}-${trip.to}-${trip.date}`;

        result.push({
          ...item,
          key: item.key || `reservation-${parentKey}-trip-${childKey}`,
          tripIndex,
          totalTrips: trips.length,
          tripBusinessItem: trip,
          rowSpan: tripIndex === 0 ? trips.length : 0, // Chỉ row đầu tiên có rowSpan
          originalIndex: itemIndex, // Lưu index gốc để tính STT
        });
      });
    });

    return result;
  }, [reservationRequests]);

  const handleSelect = (checked: boolean, key: string) => {
    setSelectedRowKeys((prevKeys) => {
      if (checked) {
        // Thêm key nếu chưa có
        if (prevKeys.includes(key)) {
          return prevKeys;
        }
        return [...prevKeys, key];
      } else {
        // Xóa key
        return prevKeys.filter((k) => k !== key);
      }
    });
  };

  // Xử lý select ở cột đầu tiên (có rowSpan) - select tất cả các row con (hành trình) của reservation này
  const handleSelectFirstColumn = (checked: boolean, record: ExpandedRow) => {
    // Chỉ xử lý khi record có rowSpan (hàng đầu tiên của mỗi reservation)
    if (record.rowSpan && record.rowSpan > 0) {
      // Tìm tất cả các row con (hành trình) của cùng một reservation request
      const childRows = expandedDataSource.filter((row) => row.originalIndex === record.originalIndex);
      const childRowKeys = childRows.map((row) => row.key || "").filter(Boolean);

      setSelectedRowKeys((prevKeys) => {
        if (checked) {
          // Thêm tất cả các key của row con nếu chưa có
          const newKeys = childRowKeys.filter((key) => !prevKeys.includes(key));
          return [...prevKeys, ...newKeys];
        } else {
          // Xóa tất cả các key của row con
          return prevKeys.filter((k) => !childRowKeys.includes(String(k)));
        }
      });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Chọn tất cả các hàng
      const allKeys = expandedDataSource.map((record) => record.key || "").filter(Boolean);
      setSelectedRowKeys(allKeys);
    } else {
      // Bỏ chọn tất cả
      setSelectedRowKeys([]);
    }
  };

  const isAllSelected =
    expandedDataSource.length > 0 && expandedDataSource.every((record) => selectedRowKeys.includes(record.key || ""));

  const isIndeterminate = selectedRowKeys.length > 0 && selectedRowKeys.length < expandedDataSource.length;

  const columns: ColumnsType<ExpandedRow> = [
    {
      title: (
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: "select",
      align: "center",
      render: (_: any, record: ExpandedRow) => {
        // Chỉ hiển thị checkbox ở hàng đầu tiên (có rowSpan)
        // Checkbox này select tất cả các row con (hành trình) của reservation này
        const isFirstRow = record.rowSpan && record.rowSpan > 0;

        if (!isFirstRow) {
          return {
            children: null,
            props: {
              rowSpan: 0,
            },
          };
        }

        // Tìm tất cả các row con (hành trình) của cùng một reservation request
        const childRows = expandedDataSource.filter((row) => row.originalIndex === record.originalIndex);
        const childRowKeys = childRows.map((row) => row.key || "").filter(Boolean);
        const selectedChildKeys = childRowKeys.filter((key) => selectedRowKeys.includes(key));

        const allChildrenSelected = childRowKeys.length > 0 && selectedChildKeys.length === childRowKeys.length;
        const someChildrenSelected = selectedChildKeys.length > 0 && selectedChildKeys.length < childRowKeys.length;

        return {
          children: (
            <Checkbox
              checked={allChildrenSelected}
              indeterminate={someChildrenSelected}
              onChange={(e) => handleSelectFirstColumn(e.target.checked, record)}
            />
          ),
          props: {
            rowSpan: record.rowSpan || 0,
          },
        };
      },
    },
    {
      title: "STT.",
      key: "stt",
      width: 60,
      align: "center",
      render: (_: any, record: ExpandedRow) => {
        const obj = {
          children: (record.originalIndex ?? 0) + 1,
          props: {
            rowSpan: record.rowSpan || 0,
          },
        };
        return obj;
      },
    },
    {
      title: "Họ và tên (Tiếng Việt có dấu)",
      dataIndex: "Name",
      key: "name",
      render: (text: string, record: ExpandedRow) => {
        const obj = {
          children: text || "-",
          props: {
            rowSpan: record.rowSpan || 0,
          },
        };
        return obj;
      },
    },
    {
      title: "Chức vụ",
      dataIndex: "Position",
      key: "position",
      render: (text: string, record: ExpandedRow) => {
        const obj = {
          children: text || "-",
          props: {
            rowSpan: record.rowSpan || 0,
          },
        };
        return obj;
      },
    },
    {
      title: "Hạng ghế",
      dataIndex: "Class",
      key: "class",
      render: (text: string, record: ExpandedRow) => {
        const obj = {
          children: text || "-",
          props: {
            rowSpan: record.rowSpan || 0,
          },
        };
        return obj;
      },
    },
    {
      title: "Số vé (Trường hợp hoàn vé)",
      dataIndex: "TickNumber",
      key: "tickNumber",
      render: (text: string, record: ExpandedRow) => {
        const obj = {
          children: text || "-",
          props: {
            rowSpan: record.rowSpan || 0,
          },
        };
        return obj;
      },
    },
    {
      title: "Hành trình (VD: Nơi đi: SGN, Nơi đến: DAD, Ngày: 19/09/2023 02:45CH)",
      key: "tripBusiness",
      render: (_: any, record: ExpandedRow) => formatTripBusiness(record.tripBusinessItem),
    },
    {
      title: (
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: "select2",
      align: "center",
      render: (_: any, record: ExpandedRow) => (
        <Checkbox
          checked={selectedRowKeys.includes(record.key || "")}
          onChange={(e) => handleSelect(e.target.checked, record.key || "")}
        />
      ),
    },
  ];

  useEffect(() => {
    // Tìm các originalIndex của các row được chọn
    const selectedOriginalIndices = new Set<number>();
    expandedDataSource.forEach((row) => {
      if (selectedRowKeys.includes(row.key || "")) {
        selectedOriginalIndices.add(row.originalIndex ?? -1);
      }
    });

    // Filter reservationRequests và chỉ giữ lại các hành trình được chọn
    const filtered = (reservationRequests || [])
      .map((item, itemIndex) => {
        // Chỉ xử lý nếu reservation này có ít nhất một hành trình được chọn
        if (!selectedOriginalIndices.has(itemIndex)) {
          return null;
        }

        // Tìm các hành trình được chọn của reservation này
        const trips = Array.isArray(item.TripBusiness)
          ? item.TripBusiness
          : item.TripBusiness
          ? [item.TripBusiness]
          : [];

        const selectedTrips = trips.filter((trip: any) => {
          // Tạo key giống như trong expandedDataSource
          const parentKey = `${item.Name}-${item.Position}-${item.Department}`;
          const from = trip.from || trip.From || "";
          const to = trip.to || trip.To || "";
          const date = trip.date || trip.Date || "";
          const childKey = `${from}-${to}-${date}`;
          const rowKey = item.key || `reservation-${parentKey}-trip-${childKey}`;
          return selectedRowKeys.includes(rowKey);
        });

        // Nếu không có hành trình nào được chọn, bỏ qua
        if (selectedTrips.length === 0) {
          return null;
        }

        // Trả về reservation với TripBusiness đã được filter
        return {
          ...item,
          TripBusiness: selectedTrips,
        };
      })
      .filter((item) => item !== null);

    setProcessedData(filtered);
  }, [selectedRowKeys, reservationRequests, expandedDataSource, setProcessedData]);

  // Chỉ set selectedRowKeys lần đầu tiên khi có processedData (khi edit)
  useEffect(() => {
    // Chỉ chạy một lần khi có processedData và chưa được initialize
    if (
      !hasInitializedFromProcessedData.current &&
      processedData &&
      Array.isArray(processedData) &&
      processedData.length > 0
    ) {
      const allKeys: React.Key[] = [];

      processedData.forEach((item) => {
        const trips = Array.isArray(item.TripBusiness)
          ? item.TripBusiness
          : item.TripBusiness
          ? [item.TripBusiness]
          : [];

        const parentKey = `${item.Name}-${item.Position}-${item.Department}`;

        trips.forEach((trip: any) => {
          const from = trip.from || trip.From || "";
          const to = trip.to || trip.To || "";
          const date = trip.date || trip.Date || "";
          const childKey = `${from}-${to}-${date}`;
          const rowKey = item.key || `reservation-${parentKey}-trip-${childKey}`;
          allKeys.push(rowKey);
        });
      });

      setSelectedRowKeys(allKeys);
      hasInitializedFromProcessedData.current = true;
    }
  }, [processedData]);

  return <Table columns={columns} dataSource={expandedDataSource} pagination={false} bordered size="small" />;
};

export default ReservationRequestTable;
